class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Delegated click handlers survive innerHTML replacements
    this.addEventListener('click', (event) => {
      const addBtn = event.target.closest('.ck-upsell__add');
      if (addBtn && !addBtn.disabled) {
        this.handleUpsellAdd({ currentTarget: addBtn });
        return;
      }

      const arrow = event.target.closest('.ck-recs__arrow');
      if (arrow) {
        const track = this.querySelector('.ck-recs__track');
        if (track) {
          const direction = arrow.classList.contains('ck-recs__arrow--left') ? -1 : 1;
          track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
        }
      }
    });

    this.initRecsCarousel();
    this.initRecsCollapse();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Hide chatbot widget while cart drawer is open
    const chatbot = document.getElementById('crawlking-chatbot');
    if (chatbot) chatbot.style.display = 'none';
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    // Show chatbot widget again after cart drawer closes
    const chatbot = document.getElementById('crawlking-chatbot');
    if (chatbot) chatbot.style.display = '';
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.rebindOverlay();
      this.initRecsCarousel();
      this.initRecsCollapse();
      this.open();
      this.updateFloatingCartCount();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  rebindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    const newOverlay = overlay.cloneNode(true);
    overlay.parentNode.replaceChild(newOverlay, overlay);
    newOverlay.addEventListener('click', this.close.bind(this));
  }

  /* ===== Collapse/expand "You'll love these" ===== */
  initRecsCollapse() {
    const toggle = this.querySelector('.ck-recs__toggle');
    const content = this.querySelector('.ck-recs__content');
    if (!toggle || !content) return;

    // Avoid duplicate listeners after re-render
    if (toggle.dataset.bound) return;
    toggle.dataset.bound = '1';

    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      content.classList.toggle('ck-recs__content--collapsed', expanded);
    });
  }

  /* ===== Carousel arrow visibility ===== */
  initRecsCarousel() {
    const wrap = this.querySelector('.ck-recs__carousel-wrap');
    if (!wrap) return;
    const track = wrap.querySelector('.ck-recs__track');
    const leftBtn = wrap.querySelector('.ck-recs__arrow--left');
    const rightBtn = wrap.querySelector('.ck-recs__arrow--right');
    if (!track || !leftBtn || !rightBtn) return;

    const updateArrows = () => {
      leftBtn.style.display = track.scrollLeft > 4 ? '' : 'none';
      rightBtn.style.display = track.scrollLeft + track.clientWidth < track.scrollWidth - 4 ? '' : 'none';
    };

    track.addEventListener('scroll', updateArrows, { passive: true });
    updateArrows();
  }

  handleUpsellAdd(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    button.textContent = '...';

    const body = JSON.stringify({
      items: [{ id: parseInt(variantId), quantity: 1 }],
      sections: this.getSectionsToRender().map((s) => s.id),
      sections_url: window.location.pathname,
    });

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.sections) {
          this.renderContents({ sections: data.sections, id: variantId });
        } else {
          // Fallback: re-fetch the cart drawer section
          this.reloadDrawer();
        }
        this.updateFloatingCartCount();
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: data });
      })
      .catch((error) => {
        console.error('Upsell add error:', error);
        button.disabled = false;
        button.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg> Add';
      });
  }

  reloadDrawer() {
    fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('#CartDrawer');
        if (newDrawer) {
          this.classList.remove('is-empty');
          document.getElementById('CartDrawer').innerHTML = newDrawer.innerHTML;
          this.rebindOverlay();
          this.initRecsCarousel();
          this.initRecsCollapse();
        }
        // Also update icon bubble
        fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`)
          .then((r) => r.text())
          .then((bubbleHtml) => {
            const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
            const bubble = bubbleDoc.querySelector('.shopify-section');
            if (bubble) {
              const target = document.getElementById('cart-icon-bubble');
              if (target) target.innerHTML = bubble.innerHTML;
            }
          });
      });
  }

  /* ===== Floating Cart Count ===== */
  updateFloatingCartCount() {
    const countEl = document.querySelector('.ck-floating-cart__count');
    if (!countEl) return;

    fetch(window.routes.cart_url + '.js')
      .then((r) => r.json())
      .then((cart) => {
        countEl.textContent = cart.item_count;
        countEl.dataset.count = cart.item_count;
        // Pulse animation
        const btn = countEl.closest('.ck-floating-cart');
        if (btn) {
          btn.classList.remove('ck-floating-cart--pulse');
          void btn.offsetWidth; // trigger reflow
          btn.classList.add('ck-floating-cart--pulse');
        }
      });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ===== Floating Cart Button ===== */
document.addEventListener('DOMContentLoaded', () => {
  const floatingBtn = document.querySelector('.ck-floating-cart');
  if (!floatingBtn) return;

  floatingBtn.addEventListener('click', () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.open(floatingBtn);
  });

  // Update count on cart changes
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      const countEl = document.querySelector('.ck-floating-cart__count');
      if (!countEl) return;

      fetch(window.routes.cart_url + '.js')
        .then((r) => r.json())
        .then((cart) => {
          countEl.textContent = cart.item_count;
          countEl.dataset.count = cart.item_count;
          // Pulse
          const btn = countEl.closest('.ck-floating-cart');
          if (btn) {
            btn.classList.remove('ck-floating-cart--pulse');
            void btn.offsetWidth;
            btn.classList.add('ck-floating-cart--pulse');
          }
        });
    });
  }
});
